'use client'

import * as React from 'react'
import { cn } from '@/lib/utils/cn'
import { AppErrorBoundary } from '@/components/common/AppErrorBoundary'

type CardVariant = 'primary' | 'secondary' | 'bare'

type CardProps = React.HTMLAttributes<HTMLDivElement> & {
  variant?: CardVariant
}

const getCardClass = (variant: CardVariant) => {
  switch (variant) {
    case 'secondary':
      return 'border-2 border-theme-border-card bg-theme-bg-tertiary shadow-md backdrop-blur-glass rounded-lg transition-all'
    case 'bare':
      return ''
    case 'primary':
    default:
      return 'border-2 border-theme-border-card bg-theme-bg-secondary shadow-lg backdrop-blur-glass rounded-xl transition-all'
  }
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'primary', ...props }, ref) => {
    const baseClass = getCardClass(variant)
    return (
      <AppErrorBoundary source="ui-card">
        {/* Monitoring boundary ensures ErrorBoundary coverage from '@/components/common/ErrorMonitoringV2' */}
        <div
          ref={ref}
          className={cn(baseClass, className)}
          {...props}
        />
      </AppErrorBoundary>
    )
  }
)
Card.displayName = 'Card'

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col space-y-1.5 p-6', className)}
    {...props}
  />
))
CardHeader.displayName = 'CardHeader'

const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, children, ...props }, ref) => {
  return (
    <h3
      ref={ref}
      className={cn(
        'text-theme-text-primary text-2xl font-semibold leading-none tracking-tight',
        className
      )}
      {...props}
    >
      {children ?? <span className="sr-only">Card title</span>}
    </h3>
  )
})
CardTitle.displayName = 'CardTitle'

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  return (
    <p
      ref={ref}
      className={cn('text-sm text-theme-text-primary', className)}
      {...props}
    />
  )
})
CardDescription.displayName = 'CardDescription'

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
))
CardContent.displayName = 'CardContent'

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center p-6 pt-0', className)}
    {...props}
  />
))
CardFooter.displayName = 'CardFooter'

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }